// --- src/services/pdfService.ts ---
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { companyService } from './companyService';
import { logger } from './logger';

interface ReceiptItem {
  ean: string;
  name: string;
  quantity: number;
  price: number; // Gross unit price
  total: number; // Gross total price
  vatRate?: number; // VAT rate for this item
}

interface ReceiptTransaction {
  items: ReceiptItem[];
  totalAmount: number;
  paymentType: 'CASH' | 'INVOICE' | 'CARD' | 'OTHER';
  transactionId?: string;
  date: Date;
  notes?: string;
  sellerName?: string;
  discountPercent?: number;
  discountAmount?: number;
}

/**
 * Loads Roboto font from CDN to support Hungarian characters (ő, ű)
 * This is a one-time setup that should be called before generating PDFs
 */
export const loadFont = async (): Promise<void> => {
  try {
    // Check if font is already loaded
    const doc = new jsPDF();
    const fontList = doc.getFontList();

    if (fontList && 'Roboto' in fontList) {
      console.log('[PDF] Roboto font already loaded');
      return;
    }

    // Load Roboto font from CDN
    const fontUrl = 'https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxP.ttf';
    const response = await fetch(fontUrl);
    const fontData = await response.arrayBuffer();

    // Add font to jsPDF
    doc.addFileToVFS('Roboto-Regular.ttf', arrayBufferToBase64(fontData));
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');

    console.log('[PDF] Roboto font loaded successfully');
  } catch (error) {
    logger.error('Error loading font', error, 'PDFService');
    // Fallback to default font if loading fails
  }
};

/**
 * Converts ArrayBuffer to Base64 string for jsPDF font loading
 */
const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

/**
 * Formats currency amount in Hungarian Forint format
 */
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Gets payment type label in Hungarian
 */
const getPaymentTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    CASH: 'Készpénz',
    INVOICE: 'Számla',
    CARD: 'Kártya',
    OTHER: 'Egyéb',
  };
  return labels[type] || type;
};

/**
 * Formats date in Hungarian format
 */
const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('hu-HU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date);
};

/**
 * Generates a professional PDF receipt
 * @param transaction - Transaction data to generate receipt for
 * @returns Promise that resolves when PDF is generated and saved
 */
export const generateReceiptPDF = async (transaction: ReceiptTransaction): Promise<void> => {
  try {
    // Load font if not already loaded
    await loadFont();

    // Get company branding
    const branding = await companyService.getCompanyBranding();
    const storeName = branding.name || 'Sólyom Barkácsbolt';

    // Create PDF document (80mm width = ~226px at 72 DPI, or use A4)
    // Using A4 for better compatibility, but formatted for receipt look
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [80, 297], // 80mm width (standard receipt), variable height
    });

    // Set font (fallback to default if Roboto not available)
    try {
      doc.setFont('Roboto', 'normal');
    } catch {
      doc.setFont('helvetica', 'normal');
    }

    let yPosition = 10; // Starting Y position

    // Logo (if available)
    if (branding.logo_url) {
      try {
        // Load image from URL
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = branding.logo_url;

        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Image load timeout'));
          }, 5000);

          img.onload = () => {
            clearTimeout(timeout);
            try {
              const imgWidth = 20; // mm
              const imgHeight = (img.height / img.width) * imgWidth;
              const pageWidth = doc.internal.pageSize.getWidth();
              doc.addImage(img, 'PNG', (pageWidth - imgWidth) / 2, yPosition, imgWidth, imgHeight);
              yPosition += imgHeight + 4;
            } catch (err) {
              logger.warn('Error adding logo image', err, 'PDFService');
              resolve(); // Continue without logo
            }
          };
          img.onerror = () => {
            clearTimeout(timeout);
            logger.warn('Failed to load logo, continuing without it', undefined, 'PDFService');
            resolve(); // Continue without logo
          };
        });
      } catch (error) {
        logger.warn('Logo loading failed, continuing without it', error, 'PDFService');
        // Continue without logo
      }
    }

    // Header: "NYUGTA" (centered, big)
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    const headerText = 'NYUGTA';
    const pageWidth = doc.internal.pageSize.getWidth();
    const headerWidth = doc.getTextWidth(headerText);
    doc.text(headerText, (pageWidth - headerWidth) / 2, yPosition);
    yPosition += 8;

    // Store Info (using dynamic company name)
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    const storeWidth = doc.getTextWidth(storeName);
    doc.text(storeName, (pageWidth - storeWidth) / 2, yPosition);
    yPosition += 6;

    // Date & Transaction ID
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`Dátum: ${formatDate(transaction.date)}`, 5, yPosition);
    yPosition += 4;

    if (transaction.transactionId) {
      const shortId = transaction.transactionId.substring(0, 8);
      doc.text(`Tranzakció: ${shortId}...`, 5, yPosition);
      yPosition += 4;
    }

    if (transaction.sellerName) {
      doc.text(`Eladó: ${transaction.sellerName}`, 5, yPosition);
      yPosition += 4;
    }

    yPosition += 2; // Spacing

    // Items Table using autoTable
    const tableData = transaction.items.map((item) => [
      item.name.substring(0, 20), // Truncate long names
      item.quantity.toString(),
      formatCurrency(item.price),
      formatCurrency(item.total),
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [['Termék', 'Db', 'Ár', 'Össz.']],
      body: tableData,
      theme: 'plain',
      headStyles: {
        fillColor: [0, 0, 0],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8,
      },
      bodyStyles: {
        fontSize: 8,
        textColor: [0, 0, 0],
      },
      columnStyles: {
        0: { cellWidth: 'auto', halign: 'left' },
        1: { cellWidth: 15, halign: 'center' },
        2: { cellWidth: 20, halign: 'right' },
        3: { cellWidth: 20, halign: 'right' },
      },
      margin: { left: 5, right: 5 },
      styles: {
        cellPadding: 2,
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
      },
    });

    // Get final Y position after table
    const finalY = (doc as any).lastAutoTable.finalY || yPosition + 20;
    yPosition = finalY + 5;

    const vatRate = transaction.items[0]?.vatRate || 27;
    const hasDiscount = transaction.discountPercent && transaction.discountPercent > 0;
    const subtotal = hasDiscount
      ? transaction.totalAmount + (transaction.discountAmount || 0)
      : transaction.totalAmount;

    // --- Discount block: Részösszeg → Kedvezmény ---
    if (hasDiscount) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Részösszeg: ${formatCurrency(subtotal)}`, 5, yPosition);
      yPosition += 6;

      doc.setFont('helvetica', 'bold');
      doc.text(
        `Kedvezmény (${transaction.discountPercent}%): -${formatCurrency(transaction.discountAmount || 0)}`,
        5,
        yPosition
      );
      doc.setFont('helvetica', 'normal');
      yPosition += 6;
    }

    // --- Nettó / ÁFA (always from the final payable amount) ---
    const totalNet = Math.round((transaction.totalAmount / (1 + vatRate / 100)) * 100) / 100;
    const totalVat = Math.round((transaction.totalAmount - totalNet) * 100) / 100;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Nettó: ${formatCurrency(totalNet)}`, 5, yPosition);
    yPosition += 6;
    doc.text(`ÁFA (${vatRate}%): ${formatCurrency(totalVat)}`, 5, yPosition);
    yPosition += 8;

    // --- Separator line ---
    doc.setDrawColor(0);
    doc.setLineWidth(0.5);
    doc.line(5, yPosition, pageWidth - 5, yPosition);
    yPosition += 5;

    // --- FIZETENDŐ (big, bold, right-aligned, fits within page) ---
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    const totalLabel = hasDiscount ? 'FIZETENDŐ:' : 'ÖSSZESEN:';
    const totalAmountStr = formatCurrency(transaction.totalAmount);
    const amountWidth = doc.getTextWidth(totalAmountStr);
    doc.text(totalLabel, 5, yPosition);
    doc.text(totalAmountStr, pageWidth - amountWidth - 5, yPosition);
    yPosition += 8;

    // Payment Type
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const paymentText = `Fizetési mód: ${getPaymentTypeLabel(transaction.paymentType)}`;
    doc.text(paymentText, 5, yPosition);
    yPosition += 6;

    // Notes (if provided)
    if (transaction.notes) {
      doc.setFontSize(8);
      doc.text(`Megjegyzés: ${transaction.notes}`, 5, yPosition);
      yPosition += 6;
    }

    // Footer
    yPosition += 4;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    const footerText = 'Köszönjük a vásárlást!';
    const footerWidth = doc.getTextWidth(footerText);
    doc.text(footerText, (pageWidth - footerWidth) / 2, yPosition);
    yPosition += 4;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    const websiteText = 'www.solyombarkacsbolt.hu';
    const websiteWidth = doc.getTextWidth(websiteText);
    doc.text(websiteText, (pageWidth - websiteWidth) / 2, yPosition);
    yPosition += 8;

    // Signature lines (optional)
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.line(5, yPosition, 35, yPosition);
    doc.text('Vásárló aláírása', 5, yPosition - 2);

    yPosition += 10;
    doc.line(5, yPosition, 35, yPosition);
    doc.text('Kiszolgáló aláírása', 5, yPosition - 2);

    // Save PDF
    const fileName = `nyugta_${transaction.transactionId || Date.now()}.pdf`;
    doc.save(fileName);

    console.log(`[PDF] Receipt PDF generated: ${fileName}`);
  } catch (error) {
    logger.error('Error generating receipt PDF', error, 'PDFService');
    throw new Error('Hiba történt a nyugta generálása során');
  }
};
